import styled from "@emotion/styled";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Snackbar,
  Stack,
  Tooltip,
  tooltipClasses,
  TooltipProps,
} from "@mui/material";
import React from "react";

import {
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  // ArrowRightAlt,
  // ArrowForward,
} from "@mui/icons-material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridColumns,
  GridEditInputCell,
  GridRowId,
  GridRowModel,
  GridRowModes,
} from "@mui/x-data-grid";
import { Decimal } from "decimal.js";

import { LedgerTransactionApiRow } from "../../../routes/Finances/LedgerTransactionsPage";
import LedgerTransactionsToolbar from "../LedgerToolbar";
import useRowModesModel from "../../../hooks/useRowModesModel";
import GridCellExpand from "../../GridCellExpand";
import { LedgerClassificationApiRow } from "../../../routes/Finances/LedgerClassificationsPage";
import CurrencyEditInputCell from "../../DataGridEditCells/CurrencyEditInputCell";
import { LoadingButton } from "@mui/lab";
import useFetch, { CachePolicies } from "use-http";
import { useParams } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";

type AmountToDismemberProps = {
  currency: string;
  originalAmount: number;
  dismemberedAmount: number;
};

const AmountToDismember: React.FC<AmountToDismemberProps> = (props) => {
  const amountLeft = props.originalAmount - props.dismemberedAmount;
  const formattedAmountLeft = React.useMemo(() => {
    const formatted = Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: props.currency,
      signDisplay: "never",
    }).format(amountLeft);

    if (amountLeft < 0) {
      return `(${formatted})`;
    }

    return formatted;
  }, [amountLeft]);

  return (
    <span>
      <b>Valor restante à atribuir: </b>
      {formattedAmountLeft}
    </span>
  );
};

type DismemberTransactionDialogProps = {
  primaryClassifications?: LedgerClassificationApiRow[];
  secondaryClassifications?: LedgerClassificationApiRow[];
  transactionToDismember: LedgerTransactionApiRow;
  onSuccess?: () => void;
} & DialogProps;

const DismemberTransactionDialog: React.FC<DismemberTransactionDialogProps> = ({
  primaryClassifications = [],
  secondaryClassifications = [],
  transactionToDismember,
  onSuccess,
  ...props
}) => {
  const auth = useAuth();
  const { ledgerId } = useParams();
  const { post: save, loading: isSaveLoading } = useFetch(
    `${process.env.REACT_APP_API_BASE_URL}/ledger/${ledgerId}/transaction/${transactionToDismember.id}/dismember`,
    {
      cachePolicy: CachePolicies.NO_CACHE,
      headers: {
        Authorization: `Bearer ${auth.encodedToken}`,
      },
    }
  );
  const [rows, setRows] = React.useState<LedgerTransactionApiRow[]>([]);
  const originalAmount = new Decimal(transactionToDismember.amount);
  const rowsAmount = React.useMemo(() => {
    return rows.reduce((total, row) => {
      console.log("Calculating row amount:", row.amount, typeof row.amount);
      const parsed = new Decimal(row.amount || 0);
      // if (parsed.isNaN()) {
      //   parsed = new Decimal(0);
      // }

      return total.add(parsed);
    }, new Decimal(0));
  }, [rows]);
  const saveAllowed = React.useMemo(() => {
    return rowsAmount.toNumber() !== originalAmount.toNumber();
  }, [rowsAmount, originalAmount]);

  const [
    rowModesModel,
    {
      onRowEditStart,
      onRowEditStop,
      onRowModesModelChange,
      startEditingRow,
      stopEditingRow,
    },
  ] = useRowModesModel();

  const cancelEditingRow = React.useCallback(
    (id: GridRowId) => () => {
      stopEditingRow(id, true)();

      const row = rows.find((row) => row.id === id);
      if (row?.isNew) {
        setRows(rows.filter((row) => row.id !== id));
      }
    },
    [rows]
  );

  const baseClassificationColumn: Partial<GridColDef<LedgerTransactionApiRow>> =
    {
      type: "singleSelect",
      editable: true,
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <GridCellExpand
          value={params.value || ""}
          width={params.colDef.computedWidth}
        />
      ),
    };

  const columns: GridColumns<LedgerTransactionApiRow> = [
    {
      field: "actions",
      type: "actions",
      headerName: "Ações",
      width: 150,
      getActions: (params) => {
        const isEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;

        if (isEditMode) {
          return [
            <GridActionsCellItem
              icon={
                <Tooltip title="Salvar">
                  <SaveIcon />
                </Tooltip>
              }
              label="Salvar"
              onClick={stopEditingRow(params.id, false)}
              color="inherit"
            />,
            <GridActionsCellItem
              icon={
                <Tooltip title="Cancelar">
                  <CancelIcon />
                </Tooltip>
              }
              label="Cancelar"
              onClick={cancelEditingRow(params.id)}
              color="inherit"
            />,
          ];
        }
        return [
          <GridActionsCellItem
            icon={
              <Tooltip title="Editar">
                <EditIcon />
              </Tooltip>
            }
            label="Editar"
            onClick={startEditingRow(params.id)}
            color="inherit"
          />,
          <GridActionsCellItem
            disabled={
              params.row.externalId !== null ||
              params.row.dismembered ||
              Boolean(params.row.dismemberedFrom)
            }
            icon={
              <Tooltip title="Deletar">
                <DeleteIcon />
              </Tooltip>
            }
            label="Deletar"
            onClick={handleDeleteRow(params.id)}
            color="inherit"
          />,
        ];
      },
    },
    {
      field: "observation",
      headerName: "Observação",
      editable: true,
      minWidth: 160,
    },
    {
      field: "amount",
      headerName: "Valor",
      editable: true,
      minWidth: 130,
      renderEditCell: (params) => {
        const { error } = params;

        return (
          <CurrencyEditInputCell {...params} currency={params.row.currency} />
        );
      },
      valueGetter: (params) => {
        console.log("valueGetter", params.value, params.row.amount);
        const currency = params.row.currency;

        if (!isNaN(parseFloat(params.value))) {
          const parsedValue = new Decimal(params.value).div(100).toNumber();

          const formatter = Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency,
            signDisplay: "never",
          });
          const formattedValue = formatter.format(parsedValue);

          if (parsedValue < 0) {
            return `(${formattedValue})`;
          }

          return formattedValue;
        }
        return "";
      },
      preProcessEditCellProps: (params) => {
        console.log(
          "preProcessEditCellProps",
          params.props.value,
          ((params.props.value || "0") as string).replace(",", ".")
        );
        const amount = new Decimal(
          ((params.props.value || "0") as string).replace(",", ".")
        ).mul(100);

        let error: string | undefined;

        if (originalAmount.isPositive()) {
          if (amount.isNegative()) {
            error = "O valor não pode ser negativo.";
          }

          if (rowsAmount.add(amount).gt(originalAmount)) {
            error = "A soma dos valores não pode ultrapassar o valor original.";
          }
        }
        if (originalAmount.isNegative()) {
          if (amount.isPositive()) {
            error = "O valor não pode ser positivo.";
          }

          if (rowsAmount.add(amount).lt(originalAmount.toNumber())) {
            error = "A soma dos valores não pode ultrapassar o valor original.";
          }
        }

        return { ...params.props, error };
      },
      valueSetter: (params) => {
        console.log(
          "valueSetter",
          params.value,
          (params.value as string).replace(",", "."),
          isNaN(parseFloat((params.value as string).replace(",", ".")))
        );
        const resultAmount = isNaN(
          parseFloat((params.value as string).replace(",", "."))
        )
          ? params.row.amount
          : new Decimal(((params.value || "0") as string).replace(",", "."))
              .mul(100)
              .toString();

        return {
          ...params.row,
          amount: resultAmount,
        };
      },
    },
    {
      ...baseClassificationColumn,
      field: "primaryClassification",
      headerName: "Classificação",
      valueOptions: () =>
        primaryClassifications
          .filter((row) => !row.parent)
          .map((row) => ({
            label: row.name,
            value: row.id,
          })) ?? [],
      valueGetter: (params) => {
        const isEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;

        if (params.row.isNew) {
          return params.value;
        }

        if (isEditMode) {
          if (typeof params.value === "string") {
            return params.value;
          }

          return (params.value as LedgerClassificationApiRow)?.id;
        }

        if (typeof params.value === "string") {
          return primaryClassifications.find(
            (classification) => classification.id === params.value
          )?.name;
        }

        if (typeof params.row.primaryClassification === "string") {
          return primaryClassifications.find(
            (classification) =>
              classification.id === params.row.primaryClassification
          )?.name;
        }

        return params.row.primaryClassification?.name;
      },
    },
    {
      ...baseClassificationColumn,
      field: "secondaryClassification",
      headerName: "Descrição",
      valueOptions: (params) => {
        const root = secondaryClassifications.find(
          (root) => root.id === params.row?.primaryClassification
        );

        return (root?.children ?? []).map((row) => ({
          label: row.name,
          value: row.id,
        }));
      },
      valueGetter: (params) => {
        const isEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;

        if (params.row.isNew) {
          return params.value;
        }

        if (isEditMode) {
          if (typeof params.value === "string") {
            return params.value;
          }

          return (params.value as LedgerClassificationApiRow)?.id;
        }

        if (typeof params.value === "string") {
          return primaryClassifications.find(
            (classification) => classification.id === params.value
          )?.name;
        }

        if (typeof params.row.secondaryClassification === "string") {
          return primaryClassifications.find(
            (classification) =>
              classification.id === params.row.secondaryClassification
          )?.name;
        }

        return params.row.secondaryClassification?.name;
      },
    },
  ];

  const processRowUpdate = async (
    newRow: GridRowModel<LedgerTransactionApiRow>
  ) => {
    const rowToSet = {
      ...newRow,
      isNew: false,
    };

    setRows((rows) =>
      rows.map((row) => (row.id === newRow.id ? rowToSet : row))
    );

    return rowToSet;
  };

  const handleInsertRow = React.useCallback(() => {
    const index = rows.length + 1;

    const newRow: LedgerTransactionApiRow = {
      ...transactionToDismember,
      isNew: true,
      id: `new-${index}`,
      amount: "",
      externalId: null,
      primaryClassification:
        typeof transactionToDismember.primaryClassification !== "string"
          ? transactionToDismember.primaryClassification?.id ?? null
          : transactionToDismember.primaryClassification,
      secondaryClassification:
        typeof transactionToDismember.secondaryClassification !== "string"
          ? transactionToDismember.secondaryClassification?.id ?? null
          : transactionToDismember.secondaryClassification,
      // secondaryClassification: null,
      // primaryClassification: null,
      // secondaryClassification: null,
    };

    setRows((oldRows) => [...oldRows, newRow]);

    startEditingRow(newRow.id)();
  }, [transactionToDismember, rows]);

  const handleDeleteRow = React.useCallback(
    (rowId: GridRowId) => () => {
      setRows((rows) => rows.filter((row) => row.id !== rowId));
    },
    [setRows]
  );

  // Add state for Snackbar
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "error" as "error" | "warning" | "info" | "success",
  });

  // Close Snackbar handler
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Show error message function
  const showErrorMessage = (message: string) => {
    setSnackbar({
      open: true,
      message,
      severity: "error",
    });
  };

  const handleSave = React.useCallback(() => {
    const body = rows.map((row) => {
      return {
        observation: row.observation,
        amount: row.amount,
        primaryClassification:
          typeof row.primaryClassification !== "string"
            ? row.primaryClassification?.id
            : row.primaryClassification,
        secondaryClassification:
          typeof row.secondaryClassification !== "string"
            ? row.secondaryClassification?.id
            : row.secondaryClassification,
      };
    });

    save(body)
      .then(() => {
        onSuccess?.();
        props.onClose?.({}, "escapeKeyDown");
      })
      .catch((error) => {
        console.error("Error saving dismembered transactions:", error);
        showErrorMessage("Erro ao salvar as transações separadas.");
      });
  }, [rows, save, onSuccess, props]);

  return (
    <>
      <Dialog {...props} fullWidth maxWidth="md">
        <DialogTitle>Separar Transação</DialogTitle>
        <DialogContent>
          <Stack direction="column">
            <AmountToDismember
              currency={transactionToDismember.currency ?? "BRL"}
              originalAmount={originalAmount.div(100).toNumber()}
              dismemberedAmount={rowsAmount.div(100).toNumber()}
            />
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              components={{ Toolbar: LedgerTransactionsToolbar }}
              componentsProps={{
                toolbar: {
                  onInsertRow: handleInsertRow,
                },
              }}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={onRowModesModelChange}
              onRowEditStart={onRowEditStart}
              onRowEditStop={onRowEditStop}
              processRowUpdate={processRowUpdate}
              experimentalFeatures={{ newEditingApi: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => props.onClose?.({}, "escapeKeyDown")}>
            Cancelar
          </Button>
          <LoadingButton
            // loading={isDeleting}
            disabled={saveAllowed}
            loading={isSaveLoading}
            color="info"
            // onClick={handleRemoveDialogAction}
            onClick={handleSave}
          >
            Salvar
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DismemberTransactionDialog;
